// @ts-nocheck
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { fetchAllMarkets, MarketAsset, formatCurrencyValue } from "../../services/marketService";
import MarketDetailModal from "./MarketDetailModal";
import LogoLoader from "../../components/LogoLoader";
import "./Market.css";

// Formatter for provider display
function getProviderDisplayName(source?: string): string {
  switch (source) {
    case "alpha_vantage":
      return "Alpha Vantage";
    case "twelve_data":
      return "Twelve Data";
    case "coingecko":
      return "CoinGecko";
    case "ppac":
      return "PPAC · weighted avg";
    default:
      return "Yahoo Finance";
  }
}

// Format timestamp into "09:27 pm"
function formatTimeOnly(dateString?: string): string {
  if (!dateString) {
    return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }).toLowerCase();
  }
  return new Date(dateString).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }).toLowerCase();
}

// Single Reference-Styled Card Component
function MarketCardRef({ asset, onClick }: { asset: MarketAsset; onClick: () => void }) {
  const isUp = (asset.changePercent || 0) >= 0;
  const isOil = asset.symbol === "BRENT" || asset.symbol === "WTI" || asset.symbol === "INDIA_CRUDE";
  const changeColorClass = isOil ? (isUp ? "down-red" : "up-green") : (isUp ? "up-green" : "down-red");

  const status = asset.marketStatus || (Math.abs(asset.changePercent || 0) > 1.8 ? "Volatile" : Math.abs(asset.changePercent || 0) > 0.9 ? "Above normal" : "Stable");
  const dotColorClass = status === "Volatile" ? "yellow" : status === "Above normal" ? "red" : "green";

  return (
    <div
      className="market-card-ref"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
    >
      {/* Header */}
      <div className="market-card-ref-header">
        <span>{asset.name.toUpperCase()}</span>
        <span className="text-[1.1rem] leading-none">{asset.flag || ""}</span>
      </div>

      {/* Main Price */}
      <div className="market-card-ref-price">
        {formatCurrencyValue(asset.price, asset.currency)}
      </div>

      {/* Unit / Subtitle */}
      <div className="market-card-ref-unit">
        {asset.unit || "Spot Price"}
      </div>

      {/* Change Indicator */}
      <div className={`market-card-ref-change ${changeColorClass}`}>
        <span>{isUp ? "▲" : "▼"}</span>
        <span>
          {Math.abs(asset.change || 0).toFixed(2)} ({isUp ? "+" : ""}{asset.changePercent ? asset.changePercent.toFixed(2) : "0.00"}%)
        </span>
      </div>

      {/* Volatility / Market Status */}
      <div className="market-card-ref-status">
        <span className={`market-status-dot ${dotColorClass}`} />
        <span>{status}</span>
      </div>

      {/* Secondary Info (e.g. 22K Gold Rate) */}
      {asset.secondaryInfo && (
        <div className="market-card-ref-secondary">
          {asset.secondaryInfo}
        </div>
      )}

      {/* Footer Attribution + Timestamp */}
      <div className="market-card-ref-footer">
        <span>{getProviderDisplayName(asset.source)}</span>
        <span>{formatTimeOnly(asset.fetchedAt)}</span>
      </div>
    </div>
  );
}

export default function Market() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [marketData, setMarketData] = useState<{
    commodities: MarketAsset[];
    indices: MarketAsset[];
    forex: MarketAsset[];
    crypto: MarketAsset[];
  }>({
    commodities: [],
    indices: [],
    forex: [],
    crypto: [],
  });
  const [summary, setSummary] = useState<any>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<MarketAsset | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [countdown, setCountdown] = useState<number>(300); // 5 minutes

  // Initial loader state
  const [showLoader, setShowLoader] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    let progress = 0;
    const interval = setInterval(() => {
      if (progress < 100) {
        progress = Math.min(progress + Math.random() * 30, 100);
        setLoadingProgress(progress);
      } else {
        clearInterval(interval);
        setTimeout(() => setShowLoader(false), 250);
      }
    }, 60);
    return () => clearInterval(interval);
  }, []);

  const loadData = useCallback(async (isSilent = false) => {
    if (!isSilent) setRefreshing(true);
    try {
      const res = await fetchAllMarkets();
      if (res && res.success && res.data) {
        setMarketData(res.data);
        setSummary(res.summary);
        setLastUpdated(res.lastUpdated);
      }
    } catch (err) {
      console.error("Market fetch error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setCountdown(300);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadData(false);
  }, [loadData]);

  // Polling interval (5 minutes) & countdown ticker
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          loadData(true);
          return 300;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [loadData]);

  const categoryCounts = useMemo(() => {
    return {
      all:
        (marketData.commodities?.length || 0) +
        (marketData.indices?.length || 0) +
        (marketData.forex?.length || 0) +
        (marketData.crypto?.length || 0),
      commodities: marketData.commodities?.length || 0,
      indices: marketData.indices?.length || 0,
      forex: marketData.forex?.length || 0,
      crypto: marketData.crypto?.length || 0,
    };
  }, [marketData]);

  const filterList = (list: MarketAsset[]) => {
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase().trim();
    return list.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.symbol.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q)
    );
  };

  const filteredCommodities = useMemo(() => filterList(marketData.commodities || []), [marketData.commodities, searchQuery]);
  const filteredIndices = useMemo(() => filterList(marketData.indices || []), [marketData.indices, searchQuery]);
  const filteredForex = useMemo(() => filterList(marketData.forex || []), [marketData.forex, searchQuery]);
  const filteredCrypto = useMemo(() => filterList(marketData.crypto || []), [marketData.crypto, searchQuery]);

  const formatCountdown = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Global Markets Dashboard",
    "description": "Real-time commodities, stock indices, forex, and cryptocurrency market tracking dashboard.",
    "url": "https://apps.harivishnu.in/market",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "All",
    "author": {
      "@type": "Person",
      "name": "Hari Vishnu",
      "url": "https://www.harivishnu.in"
    }
  };

  if (showLoader) {
    return <LogoLoader progress={loadingProgress} />;
  }

  return (
    <>
      <Helmet>
        <title>market - harivishnu.in</title>
        <meta
          name="description"
          content="Track real-time prices, percentage changes, sparkline trends and historical charts for commodities, global stock indices, forex and crypto on the Global Markets Dashboard."
        />
        <meta
          name="keywords"
          content="markets dashboard, commodities spot price, gold price, crude oil price, global stock indices, nifty 50, s&p 500, forex usd inr, crypto btc, Hari Vishnu"
        />
        <link rel="canonical" href="https://apps.harivishnu.in/market" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <div className="market-page-container">
        {/* Hero Section */}
        <section className="market-hero">
          <div className="mx-auto w-[min(95%,1200px)]">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="market-live-dot" />
                  <span className="text-[0.78rem] font-bold tracking-wider uppercase text-emerald-600 dark:text-emerald-400">
                    Live Caching Feed
                  </span>
                </div>
                <h1 className="text-[clamp(1.9rem,3.2vw,2.9rem)] font-[800] text-post-heading tracking-tight leading-tight">
                  Global Markets Dashboard
                </h1>
                <p className="text-[0.95rem] text-[#64748b] dark:text-[#94a3b8] mt-1 max-w-[620px]">
                  Real-time market intelligence across global commodities, sovereign stock indices, currencies, and digital assets.
                </p>
              </div>

              {/* Refresh & Countdown Widget */}
              <div className="flex items-center gap-3 self-start md:self-auto">
                <div className="text-right">
                  <div className="text-[0.72rem] text-[#64748b] dark:text-[#94a3b8]">Auto-refreshing in</div>
                  <div className="text-[0.88rem] font-mono font-bold text-post-heading">
                    {formatCountdown(countdown)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => loadData(false)}
                  disabled={refreshing}
                  className="flex items-center justify-center w-9 h-9 rounded-xl border border-slider bg-bg text-text hover:border-primary-cyan transition-colors disabled:opacity-50"
                  title="Refresh now"
                  aria-label="Refresh market data"
                >
                  <i className={`fi fi-rr-refresh ${refreshing ? "animate-spin" : ""}`}></i>
                </button>
              </div>
            </div>

            {/* Pulse Stats Bar */}
            <div className="market-pulse-bar">
              <div className="market-pulse-pill">
                <i className="fi fi-rr-chart-histogram text-[#0ea5e9]"></i>
                <span>Tracked: <strong>{categoryCounts.all} Assets</strong></span>
              </div>
              {summary?.topGainer && (
                <div className="market-pulse-pill">
                  <i className="fi fi-rr-arrow-trend-up text-emerald-500"></i>
                  <span>Top Gainer: <strong>{summary.topGainer.name} (+{summary.topGainer.changePercent}%)</strong></span>
                </div>
              )}
              {summary?.topLoser && (
                <div className="market-pulse-pill">
                  <i className="fi fi-rr-arrow-trend-down text-rose-500"></i>
                  <span>Top Mover: <strong>{summary.topLoser.name} ({summary.topLoser.changePercent}%)</strong></span>
                </div>
              )}
              {lastUpdated && (
                <div className="market-pulse-pill ml-auto text-[0.76rem]">
                  <i className="fi fi-rr-clock"></i>
                  <span>Data as of {new Date(lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Main Content Area */}
        <div className="mx-auto w-[min(95%,1200px)] mt-6">
          {/* Controls Bar: Category Tabs & Search */}
          <div className="market-controls-row">
            <div className="market-nav-tabs">
              {[
                { id: "all", label: "All Markets", count: categoryCounts.all },
                { id: "commodities", label: "Commodities", count: categoryCounts.commodities },
                { id: "indices", label: "Global Indices", count: categoryCounts.indices },
                { id: "forex", label: "Forex", count: categoryCounts.forex },
                { id: "crypto", label: "Crypto", count: categoryCounts.crypto },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`market-tab-btn ${activeCategory === tab.id ? "active" : ""}`}
                  onClick={() => setActiveCategory(tab.id)}
                >
                  <span>{tab.label}</span>
                  <span className="market-tab-count">{tab.count}</span>
                </button>
              ))}
            </div>

            <div className="market-search-box">
              <i className="fi fi-rr-search text-[#94a3b8]"></i>
              <input
                type="text"
                placeholder="Search symbol, index, asset..."
                className="market-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="text-xs text-[#94a3b8] hover:text-text"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Categorized Display */}
          {loading ? (
            <div className="market-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="market-skeleton-card" />
              ))}
            </div>
          ) : (
            <>
              {/* 1. Commodities Section */}
              {(activeCategory === "all" || activeCategory === "commodities") && filteredCommodities.length > 0 && (
                <div className="market-category-section">
                  <div className="market-section-header">
                    <div className="market-section-header-left">
                      <span className="market-section-icon">🛢️</span>
                      <h2 className="market-section-title">Commodities & Energy</h2>
                    </div>
                    <span className="market-section-badge">{filteredCommodities.length} Tracked</span>
                  </div>
                  <div className="market-grid">
                    {filteredCommodities.map((asset) => (
                      <MarketCardRef
                        key={asset.symbol}
                        asset={asset}
                        onClick={() => setSelectedAsset(asset)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* 2. Global Stock Indices Section */}
              {(activeCategory === "all" || activeCategory === "indices") && filteredIndices.length > 0 && (
                <div className="market-category-section">
                  <div className="market-section-header">
                    <div className="market-section-header-left">
                      <span className="market-section-icon">📈</span>
                      <h2 className="market-section-title">Global Stock Indices</h2>
                    </div>
                    <span className="market-section-badge">{filteredIndices.length} Tracked</span>
                  </div>
                  <div className="market-grid">
                    {filteredIndices.map((asset) => (
                      <MarketCardRef
                        key={asset.symbol}
                        asset={asset}
                        onClick={() => setSelectedAsset(asset)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* 3. Forex & Currencies Section */}
              {(activeCategory === "all" || activeCategory === "forex") && filteredForex.length > 0 && (
                <div className="market-category-section">
                  <div className="market-section-header">
                    <div className="market-section-header-left">
                      <span className="market-section-icon">💱</span>
                      <h2 className="market-section-title">Foreign Exchange (Forex)</h2>
                    </div>
                    <span className="market-section-badge">{filteredForex.length} Tracked</span>
                  </div>
                  <div className="market-grid">
                    {filteredForex.map((asset) => (
                      <MarketCardRef
                        key={asset.symbol}
                        asset={asset}
                        onClick={() => setSelectedAsset(asset)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* 4. Crypto Section */}
              {(activeCategory === "all" || activeCategory === "crypto") && filteredCrypto.length > 0 && (
                <div className="market-category-section">
                  <div className="market-section-header">
                    <div className="market-section-header-left">
                      <span className="market-section-icon">🪙</span>
                      <h2 className="market-section-title">Cryptocurrencies</h2>
                    </div>
                    <span className="market-section-badge">{filteredCrypto.length} Tracked</span>
                  </div>
                  <div className="market-grid">
                    {filteredCrypto.map((asset) => (
                      <MarketCardRef
                        key={asset.symbol}
                        asset={asset}
                        onClick={() => setSelectedAsset(asset)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Empty Search / Filter state */}
              {filteredCommodities.length === 0 &&
                filteredIndices.length === 0 &&
                filteredForex.length === 0 &&
                filteredCrypto.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-center border border-slider rounded-2xl bg-bg">
                    <i className="fi fi-rr-search-alt text-4xl text-[#94a3b8] mb-3"></i>
                    <h3 className="text-lg font-bold text-post-heading">No assets match your search</h3>
                    <p className="text-sm text-[#64748b] mt-1">Try clearing the search query or changing category.</p>
                  </div>
                )}
            </>
          )}

          {/* Footer Disclosure */}
          <footer className="market-footer-disclosure">
            <p>
              Data sources: Alpha Vantage, Twelve Data, CoinGecko. Cached & refreshed automatically across trading sessions.
            </p>
            <p className="mt-1">
              For informational and educational purposes only — not financial or investment advice. Quotes may be delayed.
            </p>
          </footer>
        </div>

        {/* Interactive Detail & History Modal */}
        {selectedAsset && (
          <MarketDetailModal
            asset={selectedAsset}
            onClose={() => setSelectedAsset(null)}
          />
        )}
      </div>
    </>
  );
}
